import { CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { CanvasserType } from '@signalvote/shared';
import type { TeamDto, VendorResponse } from '@signalvote/shared';
import type { CanvasserAnalyticsFilters } from '@/lib/queryKeys';
import { formatRelativeTime } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';

const PILL_TRIGGER =
  'rounded-full glass-surface shadow-card hover:shadow-card-hover transition-all duration-200';

const ACTIVE_RING = 'ring-2 ring-primary';

interface AnalyticsFilterBarProps {
  filters: CanvasserAnalyticsFilters;
  onFiltersChange: (filters: CanvasserAnalyticsFilters) => void;
  teams: Pick<TeamDto, 'id' | 'name'>[];
  vendors: Pick<VendorResponse, 'id' | 'name'>[];
  dataUpdatedAt?: number;
}

export function AnalyticsFilterBar({
  filters,
  onFiltersChange,
  teams,
  vendors,
  dataUpdatedAt,
}: AnalyticsFilterBarProps) {
  const showVendorFilter = filters.type !== CanvasserType.Volunteer;

  const hasActiveFilters =
    filters.type != null ||
    filters.vendorId != null ||
    filters.teamId != null ||
    filters.dateFrom != null ||
    filters.dateTo != null;

  const clearFilters = () => {
    onFiltersChange({});
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      {/* Type filter */}
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Canvasser Type
        </label>
        <Select
          value={filters.type ?? 'all'}
          onValueChange={(value) => {
            const type = value === 'all' ? undefined : (value as CanvasserType);
            onFiltersChange({
              ...filters,
              type,
              vendorId: type === CanvasserType.Volunteer ? undefined : filters.vendorId,
            });
          }}
        >
          <SelectTrigger
            className={cn('h-9 w-[150px]', PILL_TRIGGER, filters.type != null && ACTIVE_RING)}
          >
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value={CanvasserType.Volunteer}>Volunteer</SelectItem>
            <SelectItem value={CanvasserType.Paid}>Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Vendor filter — hidden when type is Volunteer */}
      {showVendorFilter && (
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Vendor
          </label>
          <Select
            value={filters.vendorId ?? 'all'}
            onValueChange={(value) =>
              onFiltersChange({ ...filters, vendorId: value === 'all' ? undefined : value })
            }
          >
            <SelectTrigger
              className={cn('h-9 w-[170px]', PILL_TRIGGER, filters.vendorId != null && ACTIVE_RING)}
            >
              <SelectValue placeholder="All Vendors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vendors</SelectItem>
              {vendors.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Team filter */}
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Team
        </label>
        <Select
          value={filters.teamId ?? 'all'}
          onValueChange={(value) =>
            onFiltersChange({ ...filters, teamId: value === 'all' ? undefined : value })
          }
        >
          <SelectTrigger
            className={cn('h-9 w-[150px]', PILL_TRIGGER, filters.teamId != null && ACTIVE_RING)}
          >
            <SelectValue placeholder="All Teams" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Teams</SelectItem>
            {teams.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Date from */}
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          From
        </label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'h-9 w-[150px] justify-start text-left text-sm font-normal',
                PILL_TRIGGER,
                filters.dateFrom != null && ACTIVE_RING,
                !filters.dateFrom && 'text-muted-foreground',
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {filters.dateFrom ? format(new Date(filters.dateFrom + 'T00:00:00'), 'MMM d, yyyy') : 'Pick date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={filters.dateFrom ? new Date(filters.dateFrom + 'T00:00:00') : undefined}
              onSelect={(date) =>
                onFiltersChange({
                  ...filters,
                  dateFrom: date ? format(date, 'yyyy-MM-dd') : undefined,
                })
              }
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Date to */}
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          To
        </label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'h-9 w-[150px] justify-start text-left text-sm font-normal',
                PILL_TRIGGER,
                filters.dateTo != null && ACTIVE_RING,
                !filters.dateTo && 'text-muted-foreground',
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {filters.dateTo ? format(new Date(filters.dateTo + 'T00:00:00'), 'MMM d, yyyy') : 'Pick date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={filters.dateTo ? new Date(filters.dateTo + 'T00:00:00') : undefined}
              onSelect={(date) =>
                onFiltersChange({
                  ...filters,
                  dateTo: date ? format(date, 'yyyy-MM-dd') : undefined,
                })
              }
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Clear filters */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="h-9 gap-1 text-muted-foreground hover:text-foreground"
          onClick={clearFilters}
        >
          <X className="h-3.5 w-3.5" />
          Clear
        </Button>
      )}

      {/* Last updated timestamp */}
      <div className="ml-auto flex items-end pb-0.5">
        <span className="text-xs text-muted-foreground">
          {dataUpdatedAt
            ? `Last updated: ${formatRelativeTime(new Date(dataUpdatedAt).toISOString())}`
            : 'Loading...'}
        </span>
      </div>
    </div>
  );
}
